import { axiosApi } from "@/config/axios.config";
import { requestHandler } from "@/utils/apiUtils";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import HomeSectionTitle from "./home-section-title";
import HomeSectionDescription from "./home-section-description";
import { Skeleton } from "@/components/ui/skeleton";
import imgUrlMapper from "@/utils/imgUrl-mapping";
import { cn } from "@/lib/utils";
import ButtonOutline from "@/components/ui/button-outline";
import FadeInAnimation from "@/components/animations/FadeInAnimation";

export type PostItemModel = {
  post_id: number;
  post_title: string;
  post_description: string;
  created_at: string;
  update_at: string;
  author_id: number;
  thumbnail_url: string;
  sub_group_name: string;
  like_cnt: number;
  comment_count: number;
};

const RecentPosts = ({
  onReady,
  page,
}: {
  onReady: () => void;
  page: number;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number>(0);

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ["MAIN_CONTENTS"],
    queryFn: async () => {
      const { result: blogList } = await requestHandler<{
        result: { list: PostItemModel[] };
      }>(async () => axiosApi.get(`post?category=all&group=all`));
      return blogList.list;
    },
    staleTime: Infinity,
    enabled: page >= 2,
  });

  useEffect(() => {
    if (isSuccess) onReady();
  }, [isSuccess, onReady]);

  return (
    <div className="flex flex-col gap-10 relative py-20">
      <div className="flex flex-col gap-10 z-2" ref={triggerRef}>
        <FadeInAnimation>
          <HomeSectionTitle className="text-zinc-50 text-3xl leading-relaxed">
            기술과 경험을 기록하며,
            <br /> 더 나은 개발자로 성장합니다
          </HomeSectionTitle>
        </FadeInAnimation>

        <FadeInAnimation>
          <div className="grid xl:grid-cols-[1fr_3fr] gap-20 xl:gap-30 relative">
            {/* === 왼쪽 영역 === */}
            <div className="mb-9">
              <div className="sticky top-40 flex flex-col gap-13 items-start">
                <HomeSectionDescription>
                  <p>
                    기술과 경험, 그리고 성장의 모든 순간을 데이터처럼
                    아카이빙하며, 어제보다 단단한 개발자로 성장하고 있습니다.
                  </p>
                </HomeSectionDescription>
                <ButtonOutline
                  path="https://blog.h-creations.com"
                  name="퍼블리셔와 개발자 그사이 어딘가"
                />
              </div>
            </div>

            {/* === 오른쪽 영역 (포스트 목록) === */}
            <div className="grid gap-7 md:gap-12 grid-cols-2 ">
              {isLoading &&
                [...Array(6)].map((_, idx) => (
                  <div key={idx} className="flex flex-col gap-4">
                    <Skeleton className="aspect-14/9 w-full" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-5 w-1/2" />
                  </div>
                ))}

              {data?.slice(0, 6).map((blogMeta, idx) => (
                <div
                  key={`post:${idx}`}
                  onMouseEnter={() => setHoverIdx(idx)}
                  // onMouseLeave={() => setHoverIdx(null)}
                  onClick={() =>
                    window.open(
                      `https://blog.h-creations.com/post/${blogMeta.post_id}`,
                      "_blank"
                    )
                  }
                  className="group cursor-pointer flex flex-col gap-4 transition-all duration-500"
                >
                  {/* 이미지 영역 */}
                  <div
                    className={cn(
                      "aspect-14/9 bg-cover bg-center rounded-md transition-all duration-700 ease-in-out",
                      hoverIdx === idx
                        ? "scale-105 grayscale-0"
                        : "scale-100 grayscale"
                    )}
                    style={{
                      backgroundImage: `url(${imgUrlMapper({
                        thumbnail: blogMeta.thumbnail_url,
                      })})`,
                    }}
                  />

                  {/* 텍스트 영역 */}
                  <div className="flex flex-col">
                    <h3 className="text-lg font-medium  text-zinc-100 group-hover:text-violet-300 group-hover:underline leading-relaxed line-clamp-2 mb-2">
                      {blogMeta.post_title}
                    </h3>
                    <p className="text-sm  text-zinc-400 leading-relaxed line-clamp-2">
                      {blogMeta.post_description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FadeInAnimation>
      </div>
    </div>
  );
};

export default RecentPosts;
